import type {
  ComponentBlock,
  Ingredient,
  IngredientGroup,
  Recipe,
  RecipeComponent,
} from './types';
import { INGREDIENT_GROUP_LABELS, ingredientGroupLabel } from './types';
import { netFromGross, netPriceOf } from './costing';
import { normalizeText, parseImportNumber } from './importer';
import { isPurchaseUnit, PURCHASE_UNITS, UNITS, type PurchaseUnit, type Unit } from './units';

/**
 * Importacion masiva del catalogo: ingredientes y recetas desde una hoja de
 * calculo.
 *
 * Es el mayor bloqueo para dar de alta un restaurante: nadie teclea 200
 * ingredientes a mano. Alex lo pidio el 12 de agosto.
 *
 * Principios de diseño, aprendidos del importador de ventas:
 *
 * 1. NO exigimos un formato exacto. Las columnas se reconocen por el nombre
 *    de la cabecera, en cualquier orden, con o sin acentos, y admitiendo
 *    varios sinonimos ("producto"/"ingrediente"/"nombre").
 * 2. NADA se escribe sin que el usuario vea antes lo que va a pasar. Este
 *    modulo solo LEE y devuelve un plan: que se crea, que se actualiza y
 *    que filas no se entienden y por que.
 * 3. Los errores son por fila y con el numero de fila del Excel, para poder
 *    corregirlos en la hoja sin adivinar.
 * 4. Una fila mala no invalida el fichero: se importa lo bueno y se informa
 *    del resto.
 */

/* ------------------------------------------------------------------ */
/* Reconocimiento de columnas                                          */
/* ------------------------------------------------------------------ */

export type IngredientField =
  | 'id'
  | 'name'
  | 'brand'
  | 'presentation'
  | 'group'
  | 'supplier'
  | 'purchaseQty'
  | 'purchaseUnit'
  | 'netPrice'
  | 'grossPrice'
  | 'vatPct'
  | 'density';

export type RecipeField =
  | 'recipeName'
  | 'code'
  | 'kind'
  | 'group'
  | 'yieldCostBase'
  | 'yieldQty'
  | 'yieldUnit'
  | 'portions'
  | 'safetyMarginPct'
  | 'componentName'
  | 'qty'
  | 'unit'
  | 'wastePct'
  | 'block'
  | 'steps';

/**
 * Sinonimos aceptados por campo. El primero de cada lista es el nombre
 * "oficial" que sale en la plantilla que descarga el usuario.
 */
const INGREDIENT_HEADERS: Array<{ field: IngredientField; words: string[] }> = [
  /*
   * "Id Alexia" es la columna que anyade la EXPORTACION de ingredientes: con
   * ella la resubida actualiza por id y se puede renombrar un producto en el
   * Excel sin que se convierta en un duplicado. OJO: nada de poner 'id' a
   * secas como sinonimo — el matcher usa includes() y pescaria "unidad".
   */
  { field: 'id', words: ['id alexia'] },
  { field: 'name', words: ['producto', 'ingrediente', 'nombre', 'articulo', 'descripcion'] },
  { field: 'brand', words: ['marca'] },
  { field: 'presentation', words: ['presentacion', 'envase', 'formato'] },
  { field: 'group', words: ['grupo', 'familia', 'categoria'] },
  { field: 'supplier', words: ['proveedor', 'distribuidor'] },
  { field: 'purchaseQty', words: ['cantidad de compra', 'cantidad compra', 'cantidad', 'contenido'] },
  { field: 'purchaseUnit', words: ['unidad de compra', 'unidad compra', 'unidad', 'medida'] },
  /*
   * El bruto va ANTES que el neto a proposito: el matcher usa includes(), y
   * "Precio con IVA" contiene "precio". Y los dos van antes que el IVA, que
   * si no se quedaria con la cabecera "Precio sin IVA" (contiene "iva").
   *
   * "Precio" a secas cae en el neto, que es lo que traen los ficheros viejos
   * y las exportaciones anteriores al precio unico.
   */
  {
    field: 'grossPrice',
    words: [
      'precio con iva', 'precio con impuestos', 'precio bruto',
      'coste con iva', 'costo con iva', 'pvp con iva',
    ],
  },
  {
    field: 'netPrice',
    words: ['precio sin iva', 'precio sin impuestos', 'precio neto', 'precio', 'coste', 'costo'],
  },
  { field: 'vatPct', words: ['iva', 'impuesto'] },
  { field: 'density', words: ['densidad'] },
];

const RECIPE_HEADERS: Array<{ field: RecipeField; words: string[] }> = [
  { field: 'recipeName', words: ['receta', 'plato', 'elaboracion'] },
  { field: 'code', words: ['codigo', 'cod'] },
  { field: 'kind', words: ['tipo'] },
  { field: 'group', words: ['grupo de carta', 'grupo', 'seccion', 'categoria'] },
  /*
   * ANTES que "Rendimiento", que si no se lo queda por el includes(). Es la
   * casilla "Calcular el coste por unidad de rendimiento, en lugar de por
   * porcion" de la ficha, que hasta ahora solo se podia marcar a mano: quien
   * importaba 90 recetas tenia que entrar una por una.
   */
  {
    field: 'yieldCostBase',
    words: [
      'coste por unidad de rendimiento', 'coste por rendimiento',
      'costo por rendimiento', 'por rendimiento',
    ],
  },
  { field: 'yieldQty', words: ['rendimiento', 'produccion', 'cantidad producida'] },
  { field: 'yieldUnit', words: ['unidad de rendimiento', 'unidad rendimiento'] },
  { field: 'portions', words: ['raciones', 'porciones'] },
  { field: 'safetyMarginPct', words: ['margen de seguridad', 'margen seguridad'] },
  { field: 'componentName', words: ['ingrediente', 'componente', 'producto'] },
  { field: 'qty', words: ['cantidad neta', 'cantidad'] },
  { field: 'unit', words: ['unidad'] },
  { field: 'wastePct', words: ['merma'] },
  { field: 'block', words: ['bloque', 'tipo de linea'] },
  { field: 'steps', words: ['elaboracion paso a paso', 'pasos', 'preparacion'] },
];

/**
 * Empareja cada columna del fichero con un campo. Recorre las reglas EN
 * ORDEN y se queda con la primera que encaja, asi que las cabeceras mas
 * especificas ("cantidad de compra") tienen que ir antes que las genericas
 * ("cantidad"). Un campo solo se asigna una vez: si el fichero trae dos
 * columnas parecidas, gana la primera.
 */
export function matchHeaders<F extends string>(
  headers: string[],
  rules: Array<{ field: F; words: string[] }>,
): Array<F | undefined> {
  const usados = new Set<F>();
  return headers.map((header) => {
    const limpio = normalizeText(header);
    if (limpio === '') return undefined;
    for (const { field, words } of rules) {
      if (usados.has(field)) continue;
      if (words.some((w) => limpio === w || limpio.startsWith(`${w} `) || limpio.includes(w))) {
        usados.add(field);
        return field;
      }
    }
    return undefined;
  });
}

export const matchIngredientHeaders = (headers: string[]) =>
  matchHeaders(headers, INGREDIENT_HEADERS);

export const matchRecipeHeaders = (headers: string[]) => matchHeaders(headers, RECIPE_HEADERS);

/* ------------------------------------------------------------------ */
/* Utilidades de lectura                                               */
/* ------------------------------------------------------------------ */

const celda = <F extends string>(row: string[], cols: Array<F | undefined>, field: F): string =>
  (row[cols.indexOf(field)] ?? '').trim();

/** Reconoce una unidad escrita de cualquier manera: "Kg", "kilos", "L", "lt". */
export function parseUnit(raw: string): Unit | undefined {
  const limpio = normalizeText(raw);
  if (limpio === '') return undefined;

  const directo = (Object.keys(UNITS) as Unit[]).find(
    (u) => u === limpio || normalizeText(UNITS[u].label) === limpio,
  );
  if (directo) return directo;

  const alias: Record<string, Unit> = {
    kilo: 'kg', kilos: 'kg', kilogramo: 'kg', kilogramos: 'kg', kgs: 'kg',
    gramo: 'g', gramos: 'g', gr: 'g', grs: 'g',
    litro: 'l', litros: 'l', lt: 'l', lts: 'l',
    mililitro: 'ml', mililitros: 'ml', cc: 'ml',
    centilitro: 'cl', centilitros: 'cl',
    libra: 'lb', libras: 'lb', lbs: 'lb',
    onza: 'oz', onzas: 'oz', ozs: 'oz',
    pieza: 'pz', piezas: 'pz', pza: 'pz', pzas: 'pz', unidad: 'pz', unidades: 'pz', ud: 'pz', uds: 'pz', u: 'pz',
    docenas: 'docena', dz: 'docena',
  };
  return alias[limpio];
}

/** Reconoce el grupo de ingrediente por su etiqueta o su clave. */
export function parseIngredientGroup(raw: string): string | undefined {
  const limpio = normalizeText(raw);
  if (limpio === '') return undefined;
  const claves = Object.keys(INGREDIENT_GROUP_LABELS) as IngredientGroup[];
  const porClave = claves.find((k) => normalizeText(k) === limpio);
  if (porClave) return porClave;
  const porEtiqueta = claves.find((k) => normalizeText(INGREDIENT_GROUP_LABELS[k]) === limpio);
  if (porEtiqueta) return porEtiqueta;
  // Un grupo que no reconocemos se respeta tal cual: el tenant puede tener
  // grupos propios, y no vamos a tirar la fila por eso.
  return raw.trim();
}

/**
 * Casilla de si/no de una hoja de calculo. Alex propuso 0/1, pero por ahi
 * llegan tambien "si", "x", "true" y "verdadero" segun quien rellene.
 * Devuelve undefined si la celda esta vacia, para poder distinguir "el usuario
 * ha dicho que no" de "el usuario no ha dicho nada".
 */
export function parseSiNo(raw: string): boolean | undefined {
  const v = normalizeText(raw);
  if (v === '') return undefined;
  if (['1', 'si', 'sí', 'x', 'true', 'verdadero', 'v', 'y', 'yes'].includes(v)) return true;
  if (['0', 'no', 'false', 'falso', 'f', 'n'].includes(v)) return false;
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Ingredientes                                                        */
/* ------------------------------------------------------------------ */

export interface ImportIssue {
  /** Fila tal y como se numera en el Excel (con cabecera = 1). */
  row: number;
  message: string;
}

export interface IngredientDraft {
  name: string;
  brand?: string;
  presentation?: string;
  group: string;
  supplierName?: string;
  purchaseQty: number;
  purchaseUnit: PurchaseUnit;
  /** Siempre sin IVA: si la fila venia con el precio con IVA, ya esta convertido. */
  netPrice: number;
  vatPct: number;
  densityGPerMl?: number;
  /** Id del ingrediente existente que se va a actualizar, si lo hay. */
  existingId?: string;
}

export interface IngredientImportResult {
  /** Ingredientes nuevos que se crearan. */
  nuevos: IngredientDraft[];
  /** Ingredientes que ya existen y se actualizaran. */
  actualizados: IngredientDraft[];
  /** Proveedores citados en el fichero que todavia no existen. */
  proveedoresNuevos: string[];
  problemas: ImportIssue[];
  /** Campos que no hemos sabido localizar en la cabecera. */
  columnasNoEncontradas: IngredientField[];
}

/** Clave de identidad de un ingrediente: nombre + marca. */
export const ingredientKey = (name: string, brand?: string): string =>
  `${normalizeText(name)}|${normalizeText(brand ?? '')}`;

export function buildIngredientImport(
  rows: string[][],
  cols: Array<IngredientField | undefined>,
  existentes: Array<Pick<Ingredient, 'ingredientId' | 'name' | 'brand'>> = [],
  proveedores: Array<{ supplierId: string; name: string }> = [],
): IngredientImportResult {
  const nuevos: IngredientDraft[] = [];
  const actualizados: IngredientDraft[] = [];
  const problemas: ImportIssue[] = [];
  const proveedoresNuevos = new Set<string>();

  const obligatorias: IngredientField[] = ['name', 'purchaseQty', 'purchaseUnit'];
  const columnasNoEncontradas = obligatorias.filter((f) => !cols.includes(f));
  // Del precio vale cualquiera de las dos columnas; que falten las dos si es
  // un fichero sin precios, y eso no hay forma de importarlo.
  if (!cols.includes('netPrice') && !cols.includes('grossPrice')) {
    columnasNoEncontradas.push('netPrice');
  }
  if (columnasNoEncontradas.length > 0) {
    return { nuevos, actualizados, proveedoresNuevos: [], problemas, columnasNoEncontradas };
  }

  const porClave = new Map(existentes.map((i) => [ingredientKey(i.name, i.brand), i]));
  const porId = new Map(existentes.map((i) => [i.ingredientId, i]));
  const proveedoresConocidos = new Set(proveedores.map((p) => normalizeText(p.name)));
  const vistos = new Set<string>();

  rows.forEach((row, index) => {
    // +2: la cabecera es la fila 1 y el usuario cuenta desde 1.
    const numero = index + 2;
    const name = celda(row, cols, 'name');
    if (name === '') return; // fila vacia: ni se cuenta como problema

    const purchaseQty = parseImportNumber(celda(row, cols, 'purchaseQty'));
    if (purchaseQty === undefined || purchaseQty <= 0) {
      problemas.push({
        row: numero,
        message: `"${name}": la cantidad de compra tiene que ser un número mayor que 0.`,
      });
      return;
    }

    const purchaseUnit = parseUnit(celda(row, cols, 'purchaseUnit'));
    // "una cucharada" no es un formato de compra: se rechaza con su motivo.
    if (!isPurchaseUnit(purchaseUnit)) {
      problemas.push({
        row: numero,
        message: `"${name}": no entendemos la unidad «${celda(row, cols, 'purchaseUnit')}». Usa ${PURCHASE_UNITS.map((u) => UNITS[u].label).join(', ')}.`,
      });
      return;
    }

    /*
     * Precio: el usuario rellena la columna que tenga delante, sin IVA
     * (albaran) o con IVA (tique), y aqui se guarda SIEMPRE el neto.
     *
     * Las tres reglas son de Alex y todas dicen lo mismo: antes que guardar
     * un numero que no sabemos si es el bueno, se para la fila.
     *  1. Con las dos columnas rellenas no elegimos una: si 42,50 y 47,90 no
     *     cuadran hay un problema real debajo (un IVA mal puesto, un precio
     *     viejo) y callarlo es guardar un dato falso.
     *  2. Con el precio con IVA y el IVA vacio no hay forma de sacar el neto.
     *  3. Cambiar el IVA mas adelante no toca el neto: se recalcula el bruto.
     *     Eso lo garantiza el modelo, que solo guarda el neto.
     */
    const vatRaw = parseImportNumber(celda(row, cols, 'vatPct'));
    const netoRaw = cols.includes('netPrice')
      ? parseImportNumber(celda(row, cols, 'netPrice'))
      : undefined;
    const brutoRaw = cols.includes('grossPrice')
      ? parseImportNumber(celda(row, cols, 'grossPrice'))
      : undefined;
    const tieneNeto = netoRaw !== undefined && netoRaw > 0;
    const tieneBruto = brutoRaw !== undefined && brutoRaw > 0;

    if (tieneNeto && tieneBruto) {
      problemas.push({
        row: numero,
        message: `"${name}": has rellenado «Precio sin IVA» y «Precio con IVA». Deja solo el que tengas delante: si los dos no cuadran, el que guardáramos sería un dato falso.`,
      });
      return;
    }
    if (!tieneNeto && !tieneBruto) {
      problemas.push({ row: numero, message: `"${name}": falta el precio de compra.` });
      return;
    }
    if (tieneBruto && vatRaw === undefined) {
      problemas.push({
        row: numero,
        message: `"${name}": con el precio con IVA hace falta la columna «IVA %» rellena para poder calcular el precio sin IVA.`,
      });
      return;
    }

    const vatPct = vatRaw ?? 10;
    const netPrice = tieneNeto ? netoRaw! : netFromGross(brutoRaw!, vatPct);

    const brand = cols.includes('brand') ? celda(row, cols, 'brand') || undefined : undefined;
    const clave = ingredientKey(name, brand);

    /*
     * Identidad: primero el Id Alexia (viene de la exportacion y sobrevive a
     * un cambio de nombre en el Excel), y si no, nombre + marca. Un id que ya
     * no existe (ingrediente borrado) se ignora y la fila entra como nueva.
     */
    const idCelda = cols.includes('id') ? celda(row, cols, 'id') : '';
    const porIdExistente = idCelda ? porId.get(idCelda) : undefined;
    const identidad = porIdExistente ? `id:${porIdExistente.ingredientId}` : clave;
    if (vistos.has(identidad)) {
      problemas.push({
        row: numero,
        message: `"${name}" está repetido en el fichero: solo se importa la primera vez.`,
      });
      return;
    }
    vistos.add(identidad);

    const supplierName = cols.includes('supplier')
      ? celda(row, cols, 'supplier') || undefined
      : undefined;
    if (supplierName && !proveedoresConocidos.has(normalizeText(supplierName))) {
      proveedoresNuevos.add(supplierName);
    }

    const draft: IngredientDraft = {
      name,
      brand,
      presentation: cols.includes('presentation')
        ? celda(row, cols, 'presentation') || undefined
        : undefined,
      group: parseIngredientGroup(cols.includes('group') ? celda(row, cols, 'group') : '') ?? 'otros',
      supplierName,
      purchaseQty,
      purchaseUnit,
      netPrice,
      vatPct,
      densityGPerMl: parseImportNumber(celda(row, cols, 'density')),
    };

    const existente = porIdExistente ?? porClave.get(clave);
    if (existente) {
      actualizados.push({ ...draft, existingId: existente.ingredientId });
    } else {
      nuevos.push(draft);
    }
  });

  return {
    nuevos,
    actualizados,
    proveedoresNuevos: [...proveedoresNuevos],
    problemas,
    columnasNoEncontradas: [],
  };
}

/* ------------------------------------------------------------------ */
/* Recetas                                                             */
/* ------------------------------------------------------------------ */

/**
 * Referencia a una receta que se crea en ESTE MISMO fichero y que, por tanto,
 * todavia no tiene id. La resuelve el servidor al importar, cuando ya ha
 * creado las recetas del lote.
 */
export interface RecipeOfFileRef {
  kind: 'recipe_del_fichero';
  /** Nombre de la receta tal y como aparece en el Excel. */
  recipeName: string;
}

export type DraftRef = RecipeComponent['ref'] | RecipeOfFileRef;

export interface RecipeComponentDraft extends Omit<RecipeComponent, 'ref'> {
  /** Nombre tal y como viene en el fichero. */
  importedName: string;
  /** Ingrediente o subreceta con el que se ha emparejado. */
  ref?: DraftRef;
}

/** Donde hay que buscar el nombre de una linea. Lo dice la columna "Bloque". */
export type DondeBuscar = 'ingrediente' | 'receta' | 'cualquiera';

export interface ComponenteSinEmparejar {
  /** Nombre tal y como viene en el fichero. */
  name: string;
  /** Donde lo hemos buscado, para que el aviso mande al sitio correcto. */
  buscadoEn: DondeBuscar;
}

/**
 * La columna "Bloque" dice DOS cosas y hasta ahora solo se leia una:
 *
 *  - en que bloque de la receta va la linea (ingredientes o empaque), y
 *  - cuando pone "subreceta", DONDE hay que buscar el nombre.
 *
 * Sin lo segundo, "Sofrito base" con Bloque = subreceta se buscaba entre los
 * ingredientes, no aparecia, y el aviso mandaba al usuario a dar de alta un
 * ingrediente que no existe (bug que encontro Alex el 1 de septiembre).
 */
export function parseBloque(raw: string): { block: ComponentBlock; buscarEn: DondeBuscar } {
  const b = normalizeText(raw);
  // El empaque siempre es un producto del catalogo: una caja, una bolsa.
  if (b.startsWith('empaque') || b.startsWith('envase')) {
    return { block: 'empaque', buscarEn: 'ingrediente' };
  }
  if (b.startsWith('sub') || b.startsWith('receta') || b.startsWith('elaboracion')) {
    return { block: 'ingrediente', buscarEn: 'receta' };
  }
  if (b.startsWith('ingrediente') || b.startsWith('producto') || b.startsWith('materia')) {
    return { block: 'ingrediente', buscarEn: 'ingrediente' };
  }
  // Bloque vacio o desconocido: se busca donde sea, como se hacia siempre.
  return { block: 'ingrediente', buscarEn: 'cualquiera' };
}

export interface RecipeDraft {
  name: string;
  code?: string;
  kind: Recipe['kind'];
  group?: string;
  yieldQty: number;
  yieldUnit: Unit;
  portions: number;
  safetyMarginPct: number;
  /**
   * true  -> el coste de referencia va por unidad de rendimiento (por litro de
   *          salsa), que es lo que necesita una subreceta usada en otras.
   * false -> por racion, que es lo normal en un plato terminado.
   */
  useYieldCostAsBase: boolean;
  steps?: string;
  components: RecipeComponentDraft[];
  existingId?: string;
}

export interface RecipeImportResult {
  nuevas: RecipeDraft[];
  actualizadas: RecipeDraft[];
  /** Componentes que no hemos encontrado, con el sitio donde los buscamos. */
  sinEmparejar: ComponenteSinEmparejar[];
  problemas: ImportIssue[];
  columnasNoEncontradas: RecipeField[];
}

/**
 * Una receta ocupa VARIAS filas: una por linea de escandallo. Se agrupan por
 * el nombre de la receta, y los datos de cabecera (rendimiento, raciones...)
 * se leen de la primera fila que los traiga — asi el usuario puede repetirlos
 * en cada fila o dejarlos solo en la primera, que es como se rellena a mano.
 *
 * DOS PASADAS, y es lo importante de esta funcion. La primera lee el fichero
 * entero y apunta que recetas se van a crear; la segunda resuelve cada linea
 * contra el catalogo de ingredientes o contra esa lista. Con una sola pasada,
 * una linea que apuntaba a una subreceta creada mas abajo en el mismo Excel
 * no la encontraba: asi entraron 9 de 11 subrecetas de Alex y fallaron las
 * dos que usaban "Sofrito base", que se creaba en ese mismo fichero.
 *
 * El orden de las filas deja de importar, que es como tiene que ser: nadie
 * ordena su Excel por dependencias, y con tres niveles (Ragu -> Salsa de
 * tomate -> Sofrito base) hay casos en los que ni siquiera se podria.
 */
export function buildRecipeImport(
  rows: string[][],
  cols: Array<RecipeField | undefined>,
  ingredientes: Array<Pick<Ingredient, 'ingredientId' | 'name' | 'brand'>> = [],
  recetas: Array<Pick<Recipe, 'recipeId' | 'name'>> = [],
): RecipeImportResult {
  const problemas: ImportIssue[] = [];
  const sinEmparejar = new Map<string, ComponenteSinEmparejar>();

  const obligatorias: RecipeField[] = ['recipeName', 'componentName', 'qty', 'unit'];
  const columnasNoEncontradas = obligatorias.filter((f) => !cols.includes(f));
  if (columnasNoEncontradas.length > 0) {
    return { nuevas: [], actualizadas: [], sinEmparejar: [], problemas, columnasNoEncontradas };
  }

  // Indice de busqueda: por nombre+marca, por nombre solo y por receta.
  const porNombreMarca = new Map(ingredientes.map((i) => [ingredientKey(i.name, i.brand), i]));
  const porNombre = new Map<string, { ingredientId: string }>();
  for (const ing of ingredientes) {
    const k = normalizeText(ing.name);
    // Con dos marcas del mismo producto no adivinamos: se queda sin emparejar.
    if (porNombre.has(k)) porNombre.set(k, { ingredientId: '' });
    else porNombre.set(k, { ingredientId: ing.ingredientId });
  }
  const recetasPorNombre = new Map(recetas.map((r) => [normalizeText(r.name), r]));

  const borradores = new Map<string, RecipeDraft>();

  /* --- Pasada 1: que recetas trae el fichero --------------------------- */

  rows.forEach((row, index) => {
    const numero = index + 2;
    const recipeName = celda(row, cols, 'recipeName');
    const componentName = celda(row, cols, 'componentName');
    if (recipeName === '' && componentName === '') return;

    if (recipeName === '') {
      problemas.push({
        row: numero,
        message: `Falta el nombre de la receta a la que pertenece «${componentName}».`,
      });
      return;
    }

    const clave = normalizeText(recipeName);
    if (borradores.has(clave)) return;

    const kindRaw = normalizeText(celda(row, cols, 'kind'));
    const existente = recetasPorNombre.get(clave);
    const kind: Recipe['kind'] = kindRaw.startsWith('sub') ? 'subreceta' : 'plato';
    borradores.set(clave, {
      name: recipeName,
      code: celda(row, cols, 'code') || undefined,
      kind,
      group: celda(row, cols, 'group') || undefined,
      /*
       * Sin columna, la SUBRECETA se valora por rendimiento y el plato por
       * racion. Antes entraba todo por racion, y eso rompia a la receta padre
       * en cuanto pedia "300 g de sofrito": una subreceta valorada por racion
       * solo admite lineas en piezas (ver computeRecipeCost). Es de donde
       * salian los "Revisar" del import masivo.
       */
      useYieldCostAsBase:
        parseSiNo(cols.includes('yieldCostBase') ? celda(row, cols, 'yieldCostBase') : '') ??
        kind === 'subreceta',
      yieldQty: parseImportNumber(celda(row, cols, 'yieldQty')) ?? 1,
      yieldUnit: parseUnit(celda(row, cols, 'yieldUnit')) ?? 'pz',
      portions: Math.max(1, Math.round(parseImportNumber(celda(row, cols, 'portions')) ?? 1)),
      safetyMarginPct: parseImportNumber(celda(row, cols, 'safetyMarginPct')) ?? 10,
      steps: celda(row, cols, 'steps') || undefined,
      components: [],
      existingId: existente?.recipeId,
    });
  });

  /**
   * Busca el nombre donde toque y devuelve la referencia, o undefined.
   *
   * Una receta del fichero que TODAVIA no existe se devuelve como
   * `recipe_del_fichero`: el id no lo sabemos aqui, lo pone el servidor
   * cuando ya la ha creado.
   */
  const resolver = (nombre: string, buscarEn: DondeBuscar): DraftRef | undefined => {
    const clave = normalizeText(nombre);

    const comoReceta = (): DraftRef | undefined => {
      const enBase = recetasPorNombre.get(clave);
      if (enBase) return { kind: 'recipe', recipeId: enBase.recipeId };
      const delFichero = borradores.get(clave);
      // Si ya existia en base de datos, el borrador trae su id y se usa ese.
      if (delFichero) {
        return delFichero.existingId
          ? { kind: 'recipe', recipeId: delFichero.existingId }
          : { kind: 'recipe_del_fichero', recipeName: delFichero.name };
      }
      return undefined;
    };

    const comoIngrediente = (): DraftRef | undefined => {
      const exacto = porNombreMarca.get(ingredientKey(nombre));
      if (exacto) return { kind: 'ingredient', ingredientId: exacto.ingredientId };
      const soloNombre = porNombre.get(clave);
      // ingredientId vacio = dos marcas del mismo producto: no adivinamos.
      if (soloNombre && soloNombre.ingredientId !== '') {
        return { kind: 'ingredient', ingredientId: soloNombre.ingredientId };
      }
      return undefined;
    };

    if (buscarEn === 'receta') return comoReceta();
    if (buscarEn === 'ingrediente') return comoIngrediente();
    return comoIngrediente() ?? comoReceta();
  };

  /* --- Pasada 2: resolver cada linea ----------------------------------- */

  rows.forEach((row, index) => {
    const numero = index + 2;
    const recipeName = celda(row, cols, 'recipeName');
    const componentName = celda(row, cols, 'componentName');
    // Fila vacia, fila huerfana (ya avisada) o cabecera de receta sin linea.
    if (recipeName === '' || componentName === '') return;

    const draft = borradores.get(normalizeText(recipeName));
    if (!draft) return;

    const qty = parseImportNumber(celda(row, cols, 'qty'));
    if (qty === undefined || qty <= 0) {
      problemas.push({
        row: numero,
        message: `"${recipeName}" → «${componentName}»: la cantidad tiene que ser mayor que 0.`,
      });
      return;
    }
    const unit = parseUnit(celda(row, cols, 'unit'));
    if (!unit) {
      problemas.push({
        row: numero,
        message: `"${recipeName}" → «${componentName}»: no entendemos la unidad «${celda(row, cols, 'unit')}».`,
      });
      return;
    }

    const { block, buscarEn } = parseBloque(celda(row, cols, 'block'));
    const ref = resolver(componentName, buscarEn);
    if (!ref) sinEmparejar.set(normalizeText(componentName), { name: componentName, buscadoEn: buscarEn });

    draft.components.push({
      importedName: componentName,
      ref,
      block,
      qty,
      unit,
      wastePct: parseImportNumber(celda(row, cols, 'wastePct')) ?? 0,
    });
  });

  const todas = [...borradores.values()].filter((d) => {
    if (d.components.length === 0) {
      problemas.push({ row: 0, message: `"${d.name}" no tiene ninguna línea: se salta.` });
      return false;
    }
    return true;
  });

  return {
    nuevas: todas.filter((d) => !d.existingId),
    actualizadas: todas.filter((d) => d.existingId),
    sinEmparejar: [...sinEmparejar.values()],
    problemas,
    columnasNoEncontradas: [],
  };
}

/* ------------------------------------------------------------------ */
/* Plantilla de ejemplo                                                */
/* ------------------------------------------------------------------ */

/** Cabeceras oficiales, las que lleva la plantilla que se descarga. */
export const INGREDIENT_TEMPLATE_HEADERS = [
  'Producto',
  'Marca',
  'Grupo',
  'Proveedor',
  'Presentación',
  'Cantidad de compra',
  'Unidad de compra',
  // Se rellena UNA de las dos, la que tenga delante quien la rellena. Con las
  // dos puestas la fila no se importa: ver `buildIngredientImport`.
  'Precio sin IVA',
  'Precio con IVA',
  'IVA %',
  'Densidad g/ml',
];

/**
 * Cabeceras de la EXPORTACION de ingredientes: la plantilla mas la columna
 * "Id Alexia" al final. Es el ciclo completo de actualizacion de precios:
 * descargas lo que tienes, cambias los precios en Excel y lo resubes por el
 * importador de siempre, que con el id actualiza aunque renombres productos.
 */
export const INGREDIENT_EXPORT_HEADERS = [...INGREDIENT_TEMPLATE_HEADERS, 'Id Alexia'];

/** Filas de la exportacion, en el orden exacto de INGREDIENT_EXPORT_HEADERS. */
export function ingredientExportRows(
  ingredients: Array<
    Pick<
      Ingredient,
      | 'ingredientId'
      | 'name'
      | 'brand'
      | 'presentation'
      | 'group'
      | 'supplierId'
      | 'purchaseQty'
      | 'purchaseUnit'
      | 'netPrice'
      | 'prices'
      | 'vatPct'
      | 'densityGPerMl'
      | 'active'
    >
  >,
  suppliers: Array<{ supplierId: string; name: string }> = [],
): Array<Array<string | number>> {
  const proveedorDe = new Map(suppliers.map((s) => [s.supplierId, s.name]));
  const numero = (value: number | undefined): string | number => value ?? '';

  return ingredients
    .filter((ing) => ing.active)
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    .map((ing) => [
      ing.name,
      ing.brand ?? '',
      // La etiqueta bonita: el importador la reconoce y la vuelve a su clave.
      ingredientGroupLabel(ing.group),
      (ing.supplierId && proveedorDe.get(ing.supplierId)) || '',
      ing.presentation ?? '',
      ing.purchaseQty,
      UNITS[ing.purchaseUnit].label,
      numero(netPriceOf(ing)),
      // "Precio con IVA" sale VACIA a proposito: si viniera rellena, resubir
      // el fichero sin tocarlo chocaria con la regla de "las dos no".
      '',
      ing.vatPct,
      numero(ing.densityGPerMl),
      ing.ingredientId,
    ]);
}

export const RECIPE_TEMPLATE_HEADERS = [
  'Receta',
  'Código',
  'Tipo',
  'Grupo de carta',
  // 1 = el coste va por unidad de rendimiento (por litro de salsa); 0 = por
  // ración. Vacío: las subrecetas por rendimiento, los platos por ración.
  'Coste por rendimiento',
  'Rendimiento',
  'Unidad de rendimiento',
  'Porciones',
  'Margen de seguridad %',
  'Ingrediente',
  'Cantidad neta',
  'Unidad',
  'Merma %',
  'Bloque',
];

/*
 * La segunda fila rellena "Precio con IVA" y deja vacio el neto: es el
 * ejemplo de que se puede teclear lo que pone el tique y Alexia saca el neto.
 */
export const INGREDIENT_TEMPLATE_ROWS: string[][] = [
  ['Aceite de oliva virgen extra', 'Oro Bailén', 'Aceites', 'Distribuciones del Norte', 'garrafón', '5', 'L', '42,50', '', '10', ''],
  ['Harina de trigo', 'Molino Real', 'Secos y granos', 'Distribuciones del Norte', 'saco', '25', 'kg', '', '17,88', '10', ''],
  ['Solomillo de ternera', '', 'Carnes y pescados', 'Carnes Roma', 'granel', '1', 'kg', '28,90', '', '10', ''],
];

export const RECIPE_TEMPLATE_ROWS: string[][] = [
  ['Croquetas caseras', 'PIC001', 'plato', 'Entrantes', '0', '12', 'pz', '2', '10', 'Harina de trigo', '250', 'g', '0', 'ingrediente'],
  ['Croquetas caseras', '', '', '', '', '', '', '', '', 'Aceite de oliva virgen extra', '50', 'ml', '0', 'ingrediente'],
  ['Solomillo al pesto', 'PRI001', 'plato', 'Principales', '0', '1', 'pz', '1', '10', 'Solomillo de ternera', '220', 'g', '8', 'ingrediente'],
  // Una subreceta que rinde 2 L: se cuesta por litro, y por eso los platos que
  // la usan pueden pedirle "300 ml" en vez de raciones.
  ['Salsa de tomate', 'SUB001', 'subreceta', '', '1', '2', 'l', '1', '10', 'Tomate pera', '2500', 'g', '10', 'ingrediente'],
];
